import { Injectable } from '@angular/core';
import { animationFrameScheduler, EMPTY, from, interval, Subject } from 'rxjs';
import { debounceTime, filter, observeOn, startWith, switchMap, take, tap } from 'rxjs/operators';
import { applyGroupAlignment, getLatexLines } from './super-latex-alignment.util';

export interface AlignGroupMember {
  getContainerElement(): HTMLElement | undefined;
  getAlignChar(): string | null;
}

@Injectable({ providedIn: 'root' })
export class SuperLatexAlignGroupService {
  private readonly groups = new Map<string, Set<AlignGroupMember>>();
  private readonly triggerSubjects = new Map<string, Subject<void>>();

  register(groupName: string, member: AlignGroupMember): void {
    let group = this.groups.get(groupName);
    if (!group) {
      group = new Set();
      this.groups.set(groupName, group);
    }
    group.add(member);
    this.ensureTrigger(groupName);
    this.triggerSubjects.get(groupName)!.next();
  }

  unregister(groupName: string, member: AlignGroupMember): void {
    const group = this.groups.get(groupName);
    if (!group) {
      return;
    }
    group.delete(member);

    if (group.size === 0) {
      this.groups.delete(groupName);
      this.triggerSubjects.get(groupName)?.complete();
      this.triggerSubjects.delete(groupName);
    } else {
      this.triggerSubjects.get(groupName)?.next();
    }
  }

  notifyContentChanged(groupName: string): void {
    this.triggerSubjects.get(groupName)?.next();
  }

  private ensureTrigger(groupName: string): void {
    if (this.triggerSubjects.has(groupName)) {
      return;
    }

    const subject = new Subject<void>();
    this.triggerSubjects.set(groupName, subject);

    subject
      .pipe(
        debounceTime(200),
        switchMap(() => this.alignGroup$(groupName)),
      )
      .subscribe();
  }

  private alignGroup$(groupName: string) {
    const group = this.groups.get(groupName);
    if (!group || group.size === 0) {
      return EMPTY;
    }

    const members = Array.from(group);

    let alignChar: string | null = null;
    for (const member of members) {
      alignChar = member.getAlignChar();
      if (alignChar) {
        break;
      }
    }
    if (!alignChar) {
      return EMPTY;
    }

    return this.waitForMembersAndAlign$(members, alignChar);
  }

  private areMembersReady(members: AlignGroupMember[]): boolean {
    return members.every((member) => {
      const el = member.getContainerElement();
      if (!el) {
        return false;
      }
      const lines = getLatexLines(el);
      return lines.length > 0 && lines.every((line) => line.querySelector('.katex-html'));
    });
  }

  /**
   * Polls at 100ms intervals (up to 31 attempts / ~3s) until all group members
   * have rendered KaTeX content. Once ready, waits for fonts and an animation
   * frame before applying cross-block alignment.
   *
   * Uses switchMap in the parent pipeline so a new trigger cancels any
   * in-progress polling from a previous trigger.
   */
  private waitForMembersAndAlign$(members: AlignGroupMember[], alignChar: string) {
    return interval(100).pipe(
      startWith(0),
      take(31),
      filter(() => this.areMembersReady(members)),
      take(1),
      switchMap(() => from(document.fonts.ready)),
      observeOn(animationFrameScheduler),
      tap(() => {
        const containers = members.map((m) => m.getContainerElement()).filter((el): el is HTMLElement => !!el);
        if (containers.length > 1) {
          applyGroupAlignment(containers, alignChar);
        }
      }),
    );
  }
}
